/**
 * 同步管理器 - 统一管理数据同步
 */

import { pushToCloud, pullFromCloud, getSyncState, isSyncConfigured, checkSyncAvailable, getDeviceId } from './cloudflareClient';
import { extractUserDataPackage, mergeCloudData } from './dataExtractor';
import type { SyncResult } from './types';

export interface SyncOptions {
  /** 是否强制全量同步 */
  force?: boolean;
  /** 静默模式（不显示错误提示） */
  silent?: boolean;
}

// 同步锁：防止并发同步
let isSyncing = false;

/** 检查是否正在同步 */
export function isSyncInProgress(): boolean {
  return isSyncing;
}

/** 执行完整同步（上传 + 下载） */
export async function performSync(options: SyncOptions = {}): Promise<SyncResult> {
  // 防止并发同步
  if (isSyncing) {
    return {
      success: false,
      timestamp: Date.now(),
      error: '同步正在进行中，请稍候',
    };
  }

  isSyncing = true;

  try {
    return await doSync(options);
  } finally {
    isSyncing = false;
  }
}

/** 实际执行同步 */
async function doSync(options: SyncOptions = {}): Promise<SyncResult> {
  if (!isSyncConfigured()) {
    return {
      success: false,
      timestamp: Date.now(),
      error: '同步未配置',
    };
  }

  const isAvailable = await checkSyncAvailable();
  if (!isAvailable) {
    return {
      success: false,
      timestamp: Date.now(),
      error: '无法连接到同步服务器',
    };
  }

  try {
    // 动态导入数据库模块
    const { defaultDb: db, saveSettings } = await import('../db/indexedDb');

    // 1. 先从云端拉取
    const pullResult = await pullFromCloud();
    let downloaded = 0;
    let studyLogsRestored = 0;

    if (pullResult.success && pullResult.data) {
      const cloudData = pullResult.data;

      // 1a. 合并卡片数据
      const mergeResult = await mergeCloudData(cloudData);
      downloaded = mergeResult.merged;
      console.log('[Sync] 从云端合并卡片:', mergeResult);

      // 1b. 合并学习时间记录 (v2+)
      if (cloudData.version >= 2 && cloudData.studyLogs && Array.isArray(cloudData.studyLogs)) {
        try {
          const existingLogs = await db.studyLogs.toArray();
          const existingIds = new Set(existingLogs.map(l => l.id));
          const newLogs = cloudData.studyLogs.filter((log: any) => !existingIds.has(log.id));
          if (newLogs.length > 0) {
            await db.studyLogs.bulkAdd(newLogs);
            studyLogsRestored = newLogs.length;
          }
          console.log('[Sync] 从云端合并学习记录:', studyLogsRestored);
        } catch (logError) {
          console.warn('[Sync] 合并学习记录失败:', logError);
        }
      }

      // 1c. 合并设置（取云端更新的设置）
      if (cloudData.settings) {
        try {
          const localSettings = await db.settings.get('default');
          // 只有当本地没有设置，或云端数据更新时才合并
          if (!localSettings || (cloudData.exportedAt > (localSettings.updatedAt || 0))) {
            const settingsToMerge: any = { id: 'default' };
            if (cloudData.settings.dailyLimits) {
              settingsToMerge.dailyLimits = cloudData.settings.dailyLimits;
            }
            if (cloudData.settings.srsPreferences) {
              settingsToMerge.srsPreferences = cloudData.settings.srsPreferences;
            }
            if (cloudData.settings.studyGoal) {
              settingsToMerge.studyGoal = cloudData.settings.studyGoal;
            }
            if (cloudData.settings.weeklyGoal) {
              settingsToMerge.weeklyGoal = cloudData.settings.weeklyGoal;
            }
            await saveSettings(settingsToMerge);
            console.log('[Sync] 从云端合并设置');
          }
        } catch (settingsError) {
          console.warn('[Sync] 合并设置失败:', settingsError);
        }
      }

      downloaded += studyLogsRestored;
    }

    // 2. 提取本地数据并上传
    const localData = await extractUserDataPackage();
    const pushResult = await pushToCloud(localData);

    if (!pushResult.success) {
      return pushResult;
    }

    return {
      success: true,
      timestamp: Date.now(),
      uploaded: pushResult.uploaded,
      downloaded,
    };
  } catch (error) {
    return {
      success: false,
      timestamp: Date.now(),
      error: error instanceof Error ? error.message : '同步失败',
    };
  }
}

/** 仅上传本地数据 */
export async function pushOnly(): Promise<SyncResult> {
  if (!isSyncConfigured()) {
    return { success: false, timestamp: Date.now(), error: '同步未配置' };
  }

  const localData = await extractUserDataPackage();
  return pushToCloud(localData);
}

/** 仅从云端下载 */
export async function pullOnly(): Promise<SyncResult> {
  if (!isSyncConfigured()) {
    return { success: false, timestamp: Date.now(), error: '同步未配置' };
  }

  const pullResult = await pullFromCloud();
  if (!pullResult.success) {
    return { success: false, timestamp: Date.now(), error: pullResult.error };
  }

  if (pullResult.data) {
    const mergeResult = await mergeCloudData(pullResult.data);
    return {
      success: true,
      timestamp: Date.now(),
      downloaded: mergeResult.merged,
    };
  }

  return { success: true, timestamp: Date.now(), downloaded: 0 };
}

/** 获取同步状态信息 */
export function getSyncInfo(): {
  configured: boolean;
  lastSync: Date | null;
  status: 'success' | 'failed' | 'never';
  pendingChanges: number;
} {
  const state = getSyncState();
  return {
    configured: isSyncConfigured(),
    lastSync: state.lastSyncAt ? new Date(state.lastSyncAt) : null,
    status: state.lastSyncStatus,
    pendingChanges: state.pendingChanges,
  };
}

/** 导出为本地文件（备份） */
export async function exportToFile(): Promise<void> {
  const data = await extractUserDataPackage();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `enpass-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** 导出完整备份（包含影子跟读数据） */
export async function exportFullBackup(): Promise<void> {
  const { exportShadowData } = await import('./shadowExport');

  // 导出主数据
  const mainData = await extractUserDataPackage();

  // 导出影子跟读数据
  const shadowData = await exportShadowData();

  const fullBackup = {
    version: 3, // v3 包含影子跟读数据
    exportedAt: Date.now(),
    main: mainData,
    shadow: shadowData,
  };

  const jsonStr = JSON.stringify(fullBackup);
  const sizeMB = jsonStr.length / 1024 / 1024;
  console.log(`[Full Backup] 总大小: ${sizeMB.toFixed(2)} MB`);

  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `enpass-full-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** 导入完整备份（自动识别格式） */
export async function importFullBackup(file: File): Promise<SyncResult & {
  shadowImported?: { materials: number; videos: number; dialogs: number };
}> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    // v3 完整备份格式
    if (data.version === 3 && data.main && data.shadow) {
      console.log('[Import] 检测到 v3 完整备份格式');

      // 导入主数据
      const mainResult = await importFromFile(
        new File([JSON.stringify(data.main)], 'main.json', { type: 'application/json' })
      );

      // 导入影子跟读数据
      const { importShadowData } = await import('./shadowExport');
      const shadowResult = await importShadowData(data.shadow);

      return {
        ...mainResult,
        shadowImported: {
          materials: shadowResult.materialsImported,
          videos: shadowResult.videosImported,
          dialogs: shadowResult.dialogsImported,
        },
      };
    }

    // 旧版本格式，只有主数据
    return importFromFile(file);
  } catch (error) {
    return {
      success: false,
      timestamp: Date.now(),
      error: error instanceof Error ? error.message : '导入失败',
    };
  }
}

/** 从本地文件导入（恢复） */
export async function importFromFile(file: File): Promise<SyncResult> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    // 验证数据格式
    if (!data.version || !data.cards) {
      throw new Error('无效的备份文件格式');
    }

    // 关键：强制重新加载种子数据，确保所有卡片模板存在
    // 备份可能来自不同版本，需要确保本地有对应的卡片 ID
    const { seedInitialData } = await import('../db/seedData');
    const { defaultDb: db, saveSettings } = await import('../db/indexedDb');

    let seedResult;
    try {
      seedResult = await seedInitialData(db, { force: true });
      console.log('[import] Seed result (forced):', seedResult);
    } catch (seedError) {
      console.error('[import] Seed failed:', seedError);
      return {
        success: false,
        timestamp: Date.now(),
        error: `种子数据加载失败: ${seedError instanceof Error ? seedError.message : String(seedError)}`,
      };
    }

    // 诊断：检查本地卡片数量
    const localCount = await db.cards.count();
    console.log('[import] Local cards after seed:', localCount);

    // 如果种子数据没有产生任何卡片，说明加载失败
    if (localCount === 0) {
      return {
        success: false,
        timestamp: Date.now(),
        error: `种子数据加载异常：本地卡片数为 0。Seed结果: inserted=${seedResult.inserted}, discovered=${seedResult.discovered}`,
      };
    }

    // 日志：备份文件中的卡片 ID 样本
    const backupSampleIds = data.cards.slice(0, 5).map((c: any) => c.cardId);
    console.log('[import] Backup cards sample:', backupSampleIds);

    // 检查备份中的第一个 ID 是否存在于本地
    const firstBackupId = data.cards[0]?.cardId;
    if (firstBackupId) {
      const found = await db.cards.get(firstBackupId);
      console.log('[import] First backup card lookup:', { id: firstBackupId, found: !!found });
    }

    const mergeResult = await mergeCloudData(data, { forceOverwrite: true });

    // 填充缺失的中文翻译
    const { fillMissingCnTranslations } = await import('../db/migrations/v4_addCnTranslation');
    await fillMissingCnTranslations();

    // v2: 恢复学习时间记录
    let studyLogsRestored = 0;
    if (data.version >= 2 && data.studyLogs && Array.isArray(data.studyLogs)) {
      try {
        // 获取本地已有的记录 ID，避免重复
        const existingLogs = await db.studyLogs.toArray();
        const existingIds = new Set(existingLogs.map(l => l.id));

        const newLogs = data.studyLogs.filter((log: any) => !existingIds.has(log.id));
        if (newLogs.length > 0) {
          await db.studyLogs.bulkAdd(newLogs);
          studyLogsRestored = newLogs.length;
        }
        console.log('[import] StudyLogs restored:', studyLogsRestored);
      } catch (logError) {
        console.warn('[import] Failed to restore studyLogs:', logError);
      }
    }

    // v2: 恢复完整设置
    if (data.settings) {
      try {
        const settingsToRestore: any = { id: 'default' };
        if (data.settings.dailyLimits) {
          settingsToRestore.dailyLimits = data.settings.dailyLimits;
        }
        if (data.settings.srsPreferences) {
          settingsToRestore.srsPreferences = data.settings.srsPreferences;
        }
        if (data.settings.studyGoal) {
          settingsToRestore.studyGoal = data.settings.studyGoal;
        }
        if (data.settings.weeklyGoal) {
          settingsToRestore.weeklyGoal = data.settings.weeklyGoal;
        }
        await saveSettings(settingsToRestore);
        console.log('[import] Settings restored');
      } catch (settingsError) {
        console.warn('[import] Failed to restore settings:', settingsError);
      }
    }

    // 如果大量被跳过，给用户详细的反馈
    if (mergeResult.missingCardIds.length > 0 && data.cards.length > 0) {
      console.warn('[import] Cards skipped due to ID mismatch:', {
        total: data.cards.length,
        merged: mergeResult.merged,
        skipped: mergeResult.skipped,
        missingExamples: mergeResult.missingCardIds.slice(0, 5),
      });
    }

    // 如果所有卡片都被跳过，尝试给出更多诊断信息
    if (mergeResult.merged === 0 && data.cards.length > 0) {
      // 收集诊断信息
      const sampleMissing = mergeResult.missingCardIds.slice(0, 3).join(', ');
      const sampleLocal = await db.cards.limit(3).toArray();
      const sampleLocalIds = sampleLocal.map(c => c.id).join(', ');

      return {
        success: false,
        timestamp: Date.now(),
        error: `导入失败：备份中 ${data.cards.length} 张卡片无法匹配。\n本地有 ${localCount} 张卡片，样本: [${sampleLocalIds}]\n备份样本: [${sampleMissing}]`,
      };
    }

    // 部分匹配的情况也给出提示
    if (mergeResult.skipped > 0 && mergeResult.merged > 0) {
      console.log(`[import] Partial match: ${mergeResult.merged} merged, ${mergeResult.skipped} skipped`);
    }

    // 构建成功信息
    const parts: string[] = [];
    if (mergeResult.merged > 0) parts.push(`${mergeResult.merged} 张卡片`);
    if (studyLogsRestored > 0) parts.push(`${studyLogsRestored} 条学习记录`);

    return {
      success: true,
      timestamp: Date.now(),
      downloaded: mergeResult.merged + studyLogsRestored,
    };
  } catch (error) {
    return {
      success: false,
      timestamp: Date.now(),
      error: error instanceof Error ? error.message : '导入失败',
    };
  }
}
